<template>
  <div class="version-diff-panel" @click="closeExpandedDep">
    <div v-if="sections.length === 0 && hideEmptyIcon" class="empty-text">
      <p class="empty-text__title">{{ emptyTitle }}</p>
      <p v-if="emptyMessage" class="empty-text__message">{{ emptyMessage }}</p>
    </div>
    <vd-empty v-else-if="sections.length === 0" :title="emptyTitle">
      <p>{{ emptyMessage }}</p>
    </vd-empty>
    <template v-else>
      <div class="sections">
        <div class="sections-toolbar">
          <span class="sections-toolbar__summary">
            {{ totalChanges }} changes across {{ sections.length }} categories
          </span>
          <div class="sections-toolbar__actions">
            <button type="button" class="toolbar-btn" @click="expandAll">Expand all</button>
            <button type="button" class="toolbar-btn" @click="collapseAll">Collapse all</button>
          </div>
        </div>
        <div v-for="section in sections" :key="section.key" class="section">
          <div class="section-header" :style="{ color: section.color }" @click="toggleSection(section.key)">
            <div class="section-title">
              <VdIcon store="fa" :name="section.icon" />
              <span>{{ section.title }}</span>
            </div>
            <div class="section-header-right">
              <span class="section-count">{{ section.changes.length }}</span>
              <button
                v-if="actionMode === 'add'"
                type="button"
                class="section-action-btn"
                @click.stop="emit('add-section', section.key)"
              >
                Add all
              </button>
              <VdIcon store="fa" :name="openSections[section.key] ? 'chevron-up' : 'chevron-down'" class="chevron" />
            </div>
          </div>
          <div v-if="openSections[section.key]" class="section-content">
            <div
              v-for="change in section.changes"
              :key="change.entityId"
              :class="[
                'entity-row',
                `entity-row--${change.changeType.toLowerCase()}`,
                {
                  'entity-row--warning': change.warning,
                  'entity-row--locked': actionMode && (change.locked || change.disabled),
                  'entity-row--clickable': actionMode && !change.locked && !change.disabled,
                },
              ]"
              @click="onRowClick(change)"
            >
              <div class="name-row">
                <span :class="['change-badge', `change-badge--${change.changeType.toLowerCase()}`]">
                  <VdIcon store="fa" :name="changeTypeIcon(change.changeType)" />
                  <span>{{ changeTypeLabel(change.changeType) }}</span>
                </span>
                <span :class="['entity-name', { 'entity-name--removed': change.changeType === 'REMOVED' }]">
                  {{ change.displayName }}
                </span>
                <span v-if="change.warning" class="warning-label">
                  <VdIcon store="fa" name="triangle-exclamation" class="warning-label__icon" />
                  {{ change.warningReason }}
                </span>
                <span v-if="change.locked && change.disabledReason" class="dep-auto-label">
                  {{ change.disabledReason }}
                </span>
                <span v-else-if="change.locked" class="dep-auto-label">(required)</span>
                <span v-else-if="change.disabled && change.disabledReason" class="dep-auto-label">
                  {{ change.disabledReason }}
                </span>
                <VdIcon
                  v-if="actionMode && !change.locked && !change.disabled"
                  store="fa"
                  :name="actionMode === 'add' ? 'chevron-right' : 'chevron-left'"
                  class="row-action-chevron"
                />
              </div>
              <div v-if="(showDependencies && hasDependencies(change)) || change.lastEditBy" class="details-row">
                <div v-if="showDependencies && hasDependencies(change)" class="dependencies" @click.stop>
                  <span class="requires-label">requires:</span>
                  <template v-for="(deps, type) in change.dependenciesWithChangesByType" :key="type">
                    <div v-if="deps.length" class="dep-wrapper">
                      <button
                        type="button"
                        class="dep-pill"
                        :style="{
                          backgroundColor: ENTITY_CONFIG[type]?.bgColor,
                          borderColor: ENTITY_CONFIG[type]?.color,
                          color: ENTITY_CONFIG[type]?.color,
                        }"
                        @click="toggleDepExpanded(change.entityId, type)"
                      >
                        <VdIcon store="fa" :name="ENTITY_CONFIG[type]?.icon" />
                        <span class="dep-count">{{ deps.length }}</span>
                        <span class="dep-chevron">
                          {{ isDepExpanded(change.entityId, type) ? "&#9650;" : "&#9660;" }}
                        </span>
                      </button>
                      <div v-if="isDepExpanded(change.entityId, type)" class="dep-dropdown">
                        <div class="dep-dropdown-header">
                          <VdIcon store="fa" :name="ENTITY_CONFIG[type]?.icon" />
                          <span>{{ ENTITY_CONFIG[type]?.label }}</span>
                        </div>
                        <div v-for="dep in deps" :key="dep.id" class="dep-dropdown-item">
                          {{ dep.displayName }}
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
                <div v-if="change.lastEditBy" class="last-edit-by">
                  <span class="last-edit-by__label">last editor:</span>
                  <VdIcon store="fa" name="user-pen" class="last-edit-by__icon" />
                  <span class="last-edit-by__name">{{ change.lastEditBy }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { computed, type PropType, ref, watch } from "vue";

import VdIcon from "@/components/VdIcon/VdIcon.vue";

import { CHANGE_TYPE_CONFIG, ENTITY_CONFIG } from "./versionDiff.config";
import type { ChangeType, DiffSection, EntityChange } from "./versionDiff.types";

const props = defineProps({
  sections: {
    type: Array as PropType<DiffSection[]>,
    required: true,
  },
  actionMode: {
    type: String as PropType<"add" | "remove" | null>,
    default: null,
  },
  showDependencies: {
    type: Boolean,
    default: false,
  },
  hideEmptyIcon: {
    type: Boolean,
    default: false,
  },
  emptyTitle: {
    type: String,
    default: "Nothing changed",
  },
  emptyMessage: {
    type: String,
    default: "There are no changes on this version",
  },
});

const emit = defineEmits<{
  "toggle-entity": [entityId: string];
  "add-section": [sectionKey: string];
}>();

const totalChanges = computed(() => props.sections.reduce((sum, s) => sum + s.changes.length, 0));

const openSections = ref<Record<string, boolean>>({});

watch(
  () => props.sections,
  (newSections) => {
    newSections.forEach((section) => {
      if (openSections.value[section.key] === undefined) {
        openSections.value[section.key] = true;
      }
    });
  },
  { immediate: true }
);

const toggleSection = (key: string) => {
  openSections.value[key] = !openSections.value[key];
};

const expandAll = () => {
  props.sections.forEach((section) => {
    openSections.value[section.key] = true;
  });
};

const collapseAll = () => {
  props.sections.forEach((section) => {
    openSections.value[section.key] = false;
  });
};

const hasDependencies = (change: EntityChange) =>
  Object.values(change.dependenciesWithChangesByType).some((deps) => deps.length > 0);

const changeTypeIcon = (changeType: ChangeType) => CHANGE_TYPE_CONFIG[changeType]?.icon ?? "circle-question";
const changeTypeLabel = (changeType: ChangeType) => CHANGE_TYPE_CONFIG[changeType]?.label ?? changeType;

const expandedDepKey = ref<string | null>(null);

const toggleDepExpanded = (entityId: string, type: string) => {
  const key = `${entityId}::${type}`;
  expandedDepKey.value = expandedDepKey.value === key ? null : key;
};

const isDepExpanded = (entityId: string, type: string) => expandedDepKey.value === `${entityId}::${type}`;

const closeExpandedDep = () => {
  expandedDepKey.value = null;
};

const onRowClick = (change: EntityChange) => {
  if (!props.actionMode || change.locked || change.disabled) {
    return;
  }
  emit("toggle-entity", change.entityId);
};
</script>

<style lang="scss" scoped>
.version-diff-panel {
  .empty-text {
    text-align: center;
    padding: size("L") size("M");
    color: var(--vd-base-placeholder-color);

    &__title {
      font-size: font("M");
      font-weight: 600;
      margin: 0 0 size("XS");
    }

    &__message {
      font-size: font("S");
      margin: 0;
    }
  }

  .sections-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: size("S") size("M");
    background-color: #f3f4f6;

    &__summary {
      font-size: font("S");
    }

    &__actions {
      display: flex;
      gap: size("XS");
    }

    .toolbar-btn {
      background: none;
      border: 1px solid var(--vd-input-border-color);
      border-radius: 6px;
      padding: 4px 12px;
      font-size: font("S");
      color: var(--vd-base-regular-text-color);
      cursor: pointer;
      font-family: inherit;

      &:hover {
        background-color: var(--vd-input-background-color);
      }
    }
  }

  .sections {
    display: flex;
    flex-direction: column;
    @extend %card;
    overflow: visible;
  }

  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: size("S") size("L");
    cursor: pointer;
    user-select: none;
    position: sticky;
    top: 0;
    z-index: 1;
    background-color: #f3f4f6;
    border-top: 1px solid var(--vd-input-border-color);

    &:hover {
      filter: brightness(0.95);
    }

    .section-title {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: font("M");
      font-weight: 600;

      svg {
        width: 13px;
        height: 13px;
      }
    }

    .section-header-right {
      display: flex;
      align-items: center;
      gap: size("S");

      .section-count {
        font-size: font("S");
        font-weight: 500;
        opacity: 0.7;
      }

      .section-action-btn {
        background: none;
        border: none;
        font-size: font("S");
        color: var(--vd-base-primary-color);
        cursor: pointer;
        font-family: inherit;
        padding: 0;

        &:hover {
          text-decoration: underline;
        }
      }

      .chevron {
        opacity: 0.7;
      }
    }
  }

  .entity-row {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: size("XS") size("L");
    border-top: 1px solid var(--vd-input-border-color);
    border-left: 4px solid transparent;

    &--added,
    &--published {
      border-left-color: #1e7e34;
    }

    &--updated {
      border-left-color: #1565c0;
    }

    &--removed,
    &--archived,
    &--stopped {
      border-left-color: #c0392b;
    }

    &--clickable {
      cursor: pointer;

      &:hover {
        background-color: var(--voodootune-option-hover-color);
      }
    }

    &--locked {
      > .name-row {
        opacity: 0.7;
      }

      .requires-label,
      .dep-pill {
        opacity: 0.7;
      }
    }

    &--warning {
      background-color: #fff7ed;
      border-left-color: #f59e0b;
    }

    .name-row {
      display: flex;
      align-items: center;
      gap: size("S");
      flex-wrap: wrap;

      .entity-name {
        font-size: font("M");
        color: var(--vd-base-regular-text-color);

        &--removed {
          text-decoration: line-through;
          color: #c0392b;
        }
      }

      .change-badge {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 2px 7px;
        border-radius: 10px;
        border: 1px solid;
        font-size: font("S");
        font-weight: 600;

        svg {
          width: 10px;
          height: 10px;
        }

        &--added,
        &--published {
          background-color: #e6f4ea;
          color: #1e7e34;
          border-color: #a8d5b0;
        }

        &--removed,
        &--archived,
        &--stopped {
          background-color: #fdecea;
          color: #c0392b;
          border-color: #f5b8b4;
        }

        &--updated {
          background-color: #e8f4fd;
          color: #1565c0;
          border-color: #a8cfee;
        }
      }

      .dep-auto-label {
        font-size: font("S");
        color: var(--vd-base-placeholder-color);
        font-style: italic;
      }

      .warning-label {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-size: font("S");
        color: #d97706;
        font-weight: 500;

        &__icon {
          width: 11px;
          height: 11px;
        }
      }

      .row-action-chevron {
        margin-left: auto;
        opacity: 0.4;
        width: 10px;
        height: 10px;
        flex-shrink: 0;
      }
    }

    .details-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: size("S");
    }

    .dependencies {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: size("XS");

      .requires-label {
        font-size: font("S");
        color: var(--vd-base-placeholder-color);
        font-style: italic;
      }

      .dep-wrapper {
        position: relative;
      }

      .dep-pill {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 2px 6px 2px 5px;
        border-radius: 10px;
        border: 1px solid;
        cursor: pointer;
        background: none;
        font-family: inherit;

        svg {
          width: 10px;
          height: 10px;
        }

        .dep-count {
          font-size: 11px;
          font-weight: 700;
          line-height: 1;
        }

        .dep-chevron {
          font-size: 9px;
          line-height: 1;
        }
      }

      .dep-dropdown {
        position: absolute;
        top: calc(100% + 4px);
        left: 0;
        z-index: 20;
        background-color: var(--vd-card-background-color, #fff);
        border: 1px solid var(--vd-input-border-color);
        border-radius: 6px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
        min-width: 160px;
        max-height: 200px;
        overflow-y: auto;

        .dep-dropdown-header {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 10px;
          font-size: font("S");
          font-weight: 600;
          color: var(--vd-base-regular-text-color);
          border-bottom: 1px solid var(--vd-input-border-color);

          svg {
            width: 11px;
            height: 11px;
          }
        }

        .dep-dropdown-item {
          padding: 5px 10px;
          font-size: font("S");
          color: var(--vd-base-regular-text-color);

          &:not(:last-child) {
            border-bottom: 1px solid var(--vd-input-border-color);
          }

          &:hover {
            background-color: var(--vd-input-background-color);
          }
        }
      }
    }

    .last-edit-by {
      display: flex;
      align-items: center;
      gap: size("XS");
      margin-left: auto;
      flex-shrink: 0;

      &__label {
        font-size: font("S");
        color: var(--vd-base-placeholder-color);
        font-style: italic;
      }

      &__icon {
        width: 11px;
        height: 11px;
        color: var(--vd-base-placeholder-color);
      }

      &__name {
        font-size: font("S");
        color: var(--vd-base-regular-text-color);
      }
    }
  }
}
</style>
